import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { getLatestImage } from "../services/storageService";
import { format } from "date-fns";

const LatestImage = () => {
  const [latestImage, setLatestImage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
  const [lastReloadTime, setLastReloadTime] = useState(Date.now());
  const [nextReloadTime, setNextReloadTime] = useState(null);
  const [reloadInterval, setReloadInterval] = useState(null);

  const loadImage = async () => {
    try {
      setError(null);
      const result = await getLatestImage();
      setLatestImage(result);
      setLastReloadTime(Date.now());

      // Calculate next reload time based on storage update time
      if (result.metadata && result.metadata.storageUpdatedAt) {
        const storageUpdateTime = new Date(result.metadata.storageUpdatedAt);
        // Next reload should be 5 minutes 10 seconds after storage update
        const nextReload = new Date(
          storageUpdateTime.getTime() + (5 * 60 + 10) * 1000
        );
        const now = new Date();

        // If the calculated next reload time is in the past, reload in 5 minutes from now
        if (nextReload <= now) {
          setNextReloadTime(new Date(now.getTime() + 5 * 60 * 1000));
          setCountdown(300); // 5 minutes
        } else {
          setNextReloadTime(nextReload);
          const secondsUntilReload = Math.floor((nextReload - now) / 1000);
          setCountdown(Math.max(secondsUntilReload, 60)); // Minimum 1 minute
        }

        console.log("🕐 Smart reload timing based on storage update:", {
          storageUpdateTime: storageUpdateTime,
          nextReload: nextReload,
          secondsUntilReload: Math.floor((nextReload - now) / 1000),
          countdown: Math.max(Math.floor((nextReload - now) / 1000), 60),
          now: now,
          captureTime: result.metadata.timestamp
            ? new Date(result.metadata.timestamp)
            : "N/A",
          timeDifference: result.metadata.timestamp
            ? Math.floor(
                (storageUpdateTime - new Date(result.metadata.timestamp)) / 1000
              ) + "s"
            : "N/A",
        });
      } else {
        // No storageUpdatedAt available, use default 5 minutes
        const nextReload = new Date(Date.now() + 5 * 60 * 1000);
        setNextReloadTime(nextReload);
        setCountdown(300);
        console.log("⚠️ No storageUpdatedAt found, using default 5min reload");
      }
    } catch (err) {
      console.error("Error loading latest image:", err);
      setError(err.message || "Failed to load the latest image");

      // On error, try again in 5 minutes
      const nextReload = new Date(Date.now() + 5 * 60 * 1000);
      setNextReloadTime(nextReload);
      setCountdown(300);
    } finally {
      setLoading(false);
    }
  };

  // Check if storage update time is within ±15 minutes of current German time
  const isTimeRecent = (storageUpdatedTimestamp) => {
    if (!storageUpdatedTimestamp) return false;

    const now = new Date();
    const storageUpdateTime = new Date(storageUpdatedTimestamp); // No timezone correction needed
    const diffInMinutes = Math.abs(now - storageUpdateTime) / (1000 * 60);

    return diffInMinutes <= 15;
  };

  // Format time remaining until next reload
  const formatTimeRemaining = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Calculate progress for circular indicator (0-100)
  const getCountdownProgress = () => {
    if (!nextReloadTime) return 0;

    const now = new Date();
    const totalTime = nextReloadTime - lastReloadTime;
    const elapsed = now - lastReloadTime;

    return Math.min((elapsed / totalTime) * 100, 100);
  };

  useEffect(() => {
    loadImage();
  }, []);

  // Set up dynamic reload timing when nextReloadTime changes
  useEffect(() => {
    // Clear any existing timeout
    if (reloadInterval) {
      clearTimeout(reloadInterval);
    }

    if (nextReloadTime) {
      const now = new Date();
      const timeUntilReload = nextReloadTime - now;

      if (timeUntilReload > 0) {
        console.log(
          `⏰ Setting up reload in ${Math.floor(
            timeUntilReload / 1000
          )} seconds`
        );
        const newInterval = setTimeout(() => {
          setLoading(true);
          loadImage();
        }, timeUntilReload);
        setReloadInterval(newInterval);
      }
    }

    return () => {
      if (reloadInterval) {
        clearTimeout(reloadInterval);
      }
    };
  }, [nextReloadTime]);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      if (nextReloadTime) {
        const now = new Date();
        const secondsLeft = Math.floor((nextReloadTime - now) / 1000);
        setCountdown(Math.max(secondsLeft, 0));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [nextReloadTime]);

  if (loading && !latestImage) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading latest image...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <Typography color='error' variant='h6'>
          An error occurred:
        </Typography>
        <Typography color='error' sx={{ mt: 1 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!latestImage || !latestImage.url) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <Typography>No image available at the moment.</Typography>
      </Box>
    );
  }

  const isRecent = latestImage.metadata?.storageUpdatedAt
    ? isTimeRecent(latestImage.metadata.storageUpdatedAt)
    : false;

  return (
    <Card sx={{ maxWidth: "100%", margin: "auto", boxShadow: 3 }}>
      {loading && (
        <CircularProgress
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
      <CardMedia
        component='img'
        image={latestImage.url}
        alt='Latest webcam view'
        sx={{
          width: "100%",
          height: "auto",
          maxHeight: "75vh",
          objectFit: "contain",
          opacity: loading ? 0.7 : 1,
        }}
      />
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant='h6'>Latest Image</Typography>

            {latestImage.metadata && latestImage.metadata.timestamp && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant='body2' color='text.secondary'>
                  Captured:{" "}
                  {(() => {
                    const timestamp = latestImage.metadata.timestamp;
                    const date = new Date(timestamp);
                    // Subtract 2 hours to correct for the timezone issue in Cloud Function (timestamp is 2 hours ahead)
                    const correctedDate = new Date(
                      date.getTime() - 2 * 60 * 60 * 1000
                    );
                    console.log("🕐 Timestamp debug:", {
                      original: timestamp,
                      asDate: date,
                      correctedDate: correctedDate,
                      formatted: format(correctedDate, "dd.MM.yyyy HH:mm"),
                      originalFormatted: format(date, "dd.MM.yyyy HH:mm"),
                    });
                    return format(correctedDate, "dd.MM.yyyy HH:mm");
                  })()}
                </Typography>

                {/* Status icon */}
                {isRecent ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label='Live'
                    size='small'
                    color='success'
                    variant='outlined'
                  />
                ) : (
                  <Chip
                    icon={<CancelIcon />}
                    label='Delayed'
                    size='small'
                    color='error'
                    variant='outlined'
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Countdown indicator and storage info */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {latestImage.metadata && latestImage.metadata.storageUpdatedAt && (
              <Typography variant='caption' color='text.secondary'>
                Stored:{" "}
                {(() => {
                  const storageUpdatedAt =
                    latestImage.metadata.storageUpdatedAt;
                  const date = new Date(storageUpdatedAt);
                  // No timezone correction needed - storageUpdatedAt is already in correct timezone

                  // Debug logging to identify the time discrepancy
                  console.log("🔍 Storage timestamp debug:", {
                    storageUpdatedAt: storageUpdatedAt,
                    asDate: date,
                    captureTimestamp: latestImage.metadata.timestamp,
                    captureAsDate: latestImage.metadata.timestamp
                      ? new Date(latestImage.metadata.timestamp)
                      : "N/A",
                    timeDifference: latestImage.metadata.timestamp
                      ? Math.floor(
                          (date - new Date(latestImage.metadata.timestamp)) /
                            1000
                        ) + "s"
                      : "N/A",
                  });

                  return format(date, "dd.MM.yyyy HH:mm");
                })()}
              </Typography>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant='determinate'
                  value={getCountdownProgress()}
                  size={40}
                  thickness={4}
                  sx={{
                    color: getCountdownProgress() > 80 ? "#ff9800" : "#4caf50",
                  }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: "absolute",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant='caption'
                    component='div'
                    color='text.secondary'
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {formatTimeRemaining(countdown)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant='caption' color='text.secondary'>
                Next reload
              </Typography>
            </Box>
          </Box>
        </Box>

        {latestImage.metadata && latestImage.metadata.filename && (
          <Typography
            variant='caption'
            color='text.secondary'
            display='block'
            mt={1}
          >
            Filename: {latestImage.metadata.filename}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default LatestImage;
